import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  alpha,
  useTheme,
  Grid,
  Chip,
  LinearProgress,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Divider,
} from "@mui/material";
import {
  Linkedin,
  Users,
  UserCheck,
  Send,
  MessageSquare,
  Reply,
  Megaphone,
  TrendingUp,
  Eye,
  ThumbsUp,
  UserPlus,
  Mail,
  Undo2,
  Building2,
  Bell,
  Inbox as InboxIcon,
  Clock,
  AlertTriangle,
  Activity,
  CheckCircle2,
  XCircle,
  ListFilter,
  Timer,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  fetchLIDashboard,
  fetchLIDashboardTimeseries,
  fetchLIDashboardAccountsHealth,
  fetchLIDashboardCampaignsSummary,
} from "../../../../../../redux/actions/linkedinOutreachAction";

// ── Shared small components ─────────────────────────────────────────────

const SectionTitle = ({ children, right }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
      {children}
    </Typography>
    {right}
  </Stack>
);

const MetricCard = ({ icon: Icon, label, value, suffix, color, subValue }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        transition: "all 0.2s",
        "&:hover": {
          borderColor: alpha(color || theme.palette.primary.main, 0.3),
          boxShadow: `0 4px 16px ${alpha(color || theme.palette.primary.main, 0.08)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(color || theme.palette.primary.main, 0.1),
          flexShrink: 0,
        }}
      >
        <Icon size={20} color={color || theme.palette.primary.main} strokeWidth={1.75} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500, display: "block", mb: 0.25 }}>
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: "1.35rem", color: theme.palette.text.primary, lineHeight: 1.2 }}>
          {value}
          {suffix && (
            <Typography component="span" sx={{ fontSize: "0.75rem", fontWeight: 500, color: theme.palette.text.secondary, ml: 0.5 }}>
              {suffix}
            </Typography>
          )}
        </Typography>
        {subValue && (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.25, display: "block" }}>
            {subValue}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

// Compact card used by the Action Mix section (smaller, more per row).
const ActionCard = ({ icon: Icon, label, value, success, skipped, failed, color }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.75,
        borderRadius: "10px",
        border: `1px solid ${theme.palette.divider}`,
        height: "100%",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: "8px", display: "flex",
          alignItems: "center", justifyContent: "center",
          bgcolor: alpha(color || theme.palette.primary.main, 0.1),
        }}>
          <Icon size={15} color={color || theme.palette.primary.main} strokeWidth={1.75} />
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary, flex: 1 }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontWeight: 700, fontSize: "1.25rem", lineHeight: 1.1 }}>
        {value.toLocaleString()}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
        {success > 0 && (
          <Tooltip title="Succeeded">
            <Typography variant="caption" sx={{ color: "#10b981", fontWeight: 600 }}>
              ✓ {success}
            </Typography>
          </Tooltip>
        )}
        {skipped > 0 && (
          <Tooltip title="Gracefully skipped (already done, modal closed, etc.)">
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
              ∅ {skipped}
            </Typography>
          </Tooltip>
        )}
        {failed > 0 && (
          <Tooltip title="Failed (excludes transient retries)">
            <Typography variant="caption" sx={{ color: "#ef4444", fontWeight: 600 }}>
              ✕ {failed}
            </Typography>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
};

const ACTION_META = {
  view_profile: { label: "Profile Views", icon: Eye, color: "#64748b" },
  connect: { label: "Connects Sent", icon: UserPlus, color: "#0A66C2" },
  message: { label: "Messages", icon: MessageSquare, color: "#8b5cf6" },
  inmail: { label: "InMails", icon: Mail, color: "#6366f1" },
  like_post: { label: "Post Likes", icon: ThumbsUp, color: "#f59e0b" },
  follow: { label: "Follows", icon: UserCheck, color: "#10b981" },
  withdraw_invite: { label: "Withdrawn Invites", icon: Undo2, color: "#94a3b8" },
  invite_to_follow_company: { label: "Company Invites", icon: Building2, color: "#ec4899" },
  check_notifications: { label: "Notif. Checks", icon: Bell, color: "#6b7280" },
};

const ACCOUNT_STATUS_COLOR = {
  active: "#10b981",
  warming_up: "#f59e0b",
  paused: "#94a3b8",
  suspended: "#ef4444",
  cookie_expired: "#f97316",
  banned: "#7f1d1d",
};

// ── Sub-sections ────────────────────────────────────────────────────────

const OverviewSection = ({ d }) => {
  const theme = useTheme();
  return (
    <>
      <SectionTitle>Overview</SectionTitle>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={Linkedin}
            label="Total Accounts"
            value={(d.total_accounts || 0).toLocaleString()}
            color={theme.palette.info?.main || "#0A66C2"}
            subValue={`${d.active_accounts || 0} active`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={Megaphone}
            label="Active Campaigns"
            value={(d.active_campaigns || 0).toLocaleString()}
            color="#8b5cf6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={Users}
            label="Total Leads"
            value={(d.total_leads || 0).toLocaleString()}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={UserCheck}
            label="Validated Leads"
            value={(d.validated_leads || 0).toLocaleString()}
            color="#10b981"
          />
        </Grid>
      </Grid>
    </>
  );
};

const EngagementSection = ({ d }) => {
  const theme = useTheme();
  return (
    <>
      <SectionTitle>Engagement (last 30 days)</SectionTitle>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={Send}
            label="Invites Sent"
            value={(d.total_invites_sent || 0).toLocaleString()}
            color={theme.palette.info?.main || "#6366F1"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={UserCheck}
            label="Accepted"
            value={(d.total_invites_accepted || 0).toLocaleString()}
            color="#10b981"
            subValue={`${d.acceptance_rate != null ? d.acceptance_rate.toFixed(1) : "0.0"}% acceptance rate`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={MessageSquare}
            label="Messages Sent"
            value={(d.total_messages_sent || 0).toLocaleString()}
            color="#8b5cf6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={Reply}
            label="Replies"
            value={(d.total_replies_received || 0).toLocaleString()}
            color="#f59e0b"
            subValue={`${d.reply_rate != null ? d.reply_rate.toFixed(1) : "0.0"}% reply rate`}
          />
        </Grid>
      </Grid>
    </>
  );
};

const ActionMixSection = ({ mix }) => {
  const [range, setRange] = useState("7d");
  const safeMix = mix || {};
  const suffix = range === "7d" ? "_7d" : "_30d";
  const entries = Object.entries(ACTION_META).map(([key, meta]) => {
    const m = safeMix[key] || {};
    const success = m[`success${suffix}`] || 0;
    const skipped = m[`skipped${suffix}`] || 0;
    const failed = m[`failed${suffix}`] || 0;
    return { key, meta, success, skipped, failed, total: success + skipped + failed };
  });

  return (
    <Box sx={{ mb: 3 }}>
      <SectionTitle
        right={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={range}
            onChange={(_, v) => v && setRange(v)}
            sx={{ "& .MuiToggleButton-root": { py: 0.25, px: 1.25, fontSize: "0.75rem" } }}
          >
            <ToggleButton value="7d">7d</ToggleButton>
            <ToggleButton value="30d">30d</ToggleButton>
          </ToggleButtonGroup>
        }
      >
        Action Mix
      </SectionTitle>
      <Grid container spacing={1.5}>
        {entries.map((e) => (
          <Grid
            item
            xs={6}
            sm={4}
            md={3}
            lg={true}
            key={e.key}
            sx={{ flexBasis: { lg: 0 }, flexGrow: { lg: 1 }, maxWidth: { lg: "none" }, minWidth: 0 }}
          >
            <ActionCard
              icon={e.meta.icon}
              label={e.meta.label}
              value={e.total}
              success={e.success}
              skipped={e.skipped}
              failed={e.failed}
              color={e.meta.color}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const FunnelSection = ({ funnel }) => {
  const theme = useTheme();
  const safeFunnel = funnel || {};

  const stages = [
    { key: "total_leads", label: "Total Leads", color: "#64748b" },
    { key: "validated", label: "Validated", color: "#10b981" },
    { key: "in_campaign", label: "In Campaign", color: "#6366f1" },
    { key: "connected", label: "Connected", color: "#0A66C2" },
    { key: "replied", label: "Replied", color: "#f59e0b" },
    { key: "meeting_booked", label: "Meeting", color: "#8b5cf6" },
    { key: "converted", label: "Converted", color: "#ec4899" },
  ];
  const max = Math.max(1, safeFunnel.total_leads || 0);

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, height: "100%" }}>
      <SectionTitle>Conversion Funnel</SectionTitle>
      <Stack spacing={1.25}>
        {stages.map((s, i) => {
          const count = safeFunnel[s.key] || 0;
          const prev = i > 0 ? safeFunnel[stages[i - 1].key] || 0 : 0;
          const pct = Math.round((count / max) * 100);
          const dropPct = i > 0 && prev > 0 ? Math.round((count / prev) * 100) : null;
          return (
            <Box key={s.key}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{s.label}</Typography>
                  {dropPct != null && (
                    <Chip
                      size="small"
                      label={`${dropPct}%`}
                      sx={{
                        height: 18, fontSize: "0.65rem", fontWeight: 600,
                        bgcolor: alpha(dropPct >= 30 ? "#10b981" : dropPct >= 10 ? "#f59e0b" : "#ef4444", 0.15),
                        color: dropPct >= 30 ? "#10b981" : dropPct >= 10 ? "#f59e0b" : "#ef4444",
                      }}
                    />
                  )}
                </Stack>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{count.toLocaleString()}</Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  height: 8, borderRadius: 4,
                  bgcolor: alpha(s.color, 0.12),
                  "& .MuiLinearProgress-bar": { bgcolor: s.color, borderRadius: 4 },
                }}
              />
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

const TimeseriesSection = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [days, setDays] = useState(30);
  const { dashboardTimeseries } = useSelector((s) => s.linkedinOutreach);

  useEffect(() => {
    dispatch(fetchLIDashboardTimeseries({ days }));
  }, [dispatch, days]);

  const data = (dashboardTimeseries?.data || []).map((d) => ({
    ...d,
    label: new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
  }));

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, height: "100%", display: "flex", flexDirection: "column" }}>
      <SectionTitle
        right={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={days}
            onChange={(_, v) => v && setDays(v)}
            sx={{ "& .MuiToggleButton-root": { py: 0.25, px: 1.25, fontSize: "0.75rem" } }}
          >
            <ToggleButton value={7}>7d</ToggleButton>
            <ToggleButton value={30}>30d</ToggleButton>
            <ToggleButton value={90}>90d</ToggleButton>
          </ToggleButtonGroup>
        }
      >
        Daily Activity
      </SectionTitle>
      <Box sx={{ width: "100%", flex: 1, minHeight: 300 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.06)} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
            <ReTooltip
              contentStyle={{
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8, fontSize: 12,
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
            <Bar dataKey="invites_sent" stackId="a" fill="#0A66C2" name="Invites" />
            <Bar dataKey="messages_sent" stackId="a" fill="#8b5cf6" name="Messages" />
            <Bar dataKey="profiles_viewed" stackId="a" fill="#64748b" name="Views" />
            <Bar dataKey="posts_liked" stackId="a" fill="#f59e0b" name="Likes" />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

const RatesTrendSection = () => {
  const theme = useTheme();
  const { dashboardTimeseries } = useSelector((s) => s.linkedinOutreach);

  // Rolling 7d rates (acceptance, reply) for the same range the bar chart is on.
  const series = useMemo(() => {
    const rows = dashboardTimeseries?.data || [];
    const result = [];
    for (let i = 0; i < rows.length; i++) {
      const window = rows.slice(Math.max(0, i - 6), i + 1);
      const sent = window.reduce((s, r) => s + (r.invites_sent || 0), 0);
      const acc = window.reduce((s, r) => s + (r.invites_accepted || 0), 0);
      const msg = window.reduce((s, r) => s + (r.messages_sent || 0), 0);
      const rep = window.reduce((s, r) => s + (r.replies_received || 0), 0);
      result.push({
        label: new Date(rows[i].date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        acceptance: sent > 0 ? Math.round((acc / sent) * 1000) / 10 : 0,
        reply: msg > 0 ? Math.round((rep / msg) * 1000) / 10 : 0,
      });
    }
    return result;
  }, [dashboardTimeseries]);

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, height: "100%" }}>
      <SectionTitle>Acceptance & Reply Trend (rolling 7d)</SectionTitle>
      <Box sx={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <LineChart data={series} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.06)} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} unit="%" />
            <ReTooltip
              contentStyle={{
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8, fontSize: 12,
              }}
              formatter={(v) => `${v}%`}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
            <Line type="monotone" dataKey="acceptance" stroke="#10b981" strokeWidth={2} dot={false} name="Acceptance" />
            <Line type="monotone" dataKey="reply" stroke="#f59e0b" strokeWidth={2} dot={false} name="Reply" />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

const UsageBar = ({ value, limit, color }) => {
  const theme = useTheme();
  const pct = limit > 0 ? Math.min(100, Math.round((value / limit) * 100)) : 0;
  const barColor = pct >= 90 ? "#ef4444" : pct >= 70 ? "#f59e0b" : (color || theme.palette.primary.main);
  return (
    <Box sx={{ minWidth: 110 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {value}/{limit}
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {pct}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6, borderRadius: 3,
          bgcolor: alpha(barColor, 0.12),
          "& .MuiLinearProgress-bar": { bgcolor: barColor, borderRadius: 3 },
        }}
      />
    </Box>
  );
};

const AccountsHealthSection = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { dashboardAccountsHealth } = useSelector((s) => s.linkedinOutreach);

  useEffect(() => {
    dispatch(fetchLIDashboardAccountsHealth());
  }, [dispatch]);

  if (!dashboardAccountsHealth || dashboardAccountsHealth.length === 0) return null;

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
      <SectionTitle>Account Health</SectionTitle>
      <Stack divider={<Divider />} spacing={0}>
        {dashboardAccountsHealth.map((a) => {
          const statusColor = ACCOUNT_STATUS_COLOR[a.status] || theme.palette.text.secondary;
          return (
            <Box key={a.id} sx={{ py: 1.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr 1fr 1fr 1fr" }, gap: 2, alignItems: "center" }}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {a.linkedin_email}
                  </Typography>
                  <Chip
                    size="small"
                    label={a.status.replace("_", " ")}
                    sx={{
                      height: 18, fontSize: "0.65rem", textTransform: "capitalize",
                      bgcolor: alpha(statusColor, 0.15), color: statusColor, fontWeight: 600,
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={1.5} sx={{ color: theme.palette.text.secondary }}>
                  <Typography variant="caption">
                    <TrendingUp size={11} style={{ verticalAlign: "middle", marginRight: 2 }} />
                    Health {a.health_score}
                  </Typography>
                  {a.ssi_score != null && (
                    <Typography variant="caption">SSI {a.ssi_score}</Typography>
                  )}
                  {a.warmup_day != null && a.status === "warming_up" && (
                    <Typography variant="caption">Warmup day {a.warmup_day}</Typography>
                  )}
                </Stack>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>Invites today</Typography>
                <UsageBar value={a.today.invites_sent} limit={a.limits.invites} color="#0A66C2" />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>Messages</Typography>
                <UsageBar value={a.today.messages_sent} limit={a.limits.messages} color="#8b5cf6" />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>Views</Typography>
                <UsageBar value={a.today.profiles_viewed} limit={a.limits.views} color="#64748b" />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>Replies today</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{a.today.replies_received}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>Last activity</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {a.last_activity_at ? new Date(a.last_activity_at).toLocaleString() : "—"}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

const CampaignsSummarySection = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { dashboardCampaignsSummary } = useSelector((s) => s.linkedinOutreach);

  useEffect(() => {
    dispatch(fetchLIDashboardCampaignsSummary());
  }, [dispatch]);

  if (!dashboardCampaignsSummary || dashboardCampaignsSummary.length === 0) return null;

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
      <SectionTitle>Campaigns — Performance</SectionTitle>
      <Stack divider={<Divider />} spacing={0}>
        {dashboardCampaignsSummary.map((c) => (
          <Box key={c.id} sx={{ py: 1.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr repeat(6, 1fr)" }, gap: 2, alignItems: "center" }}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                <Chip
                  size="small"
                  label={c.status}
                  sx={{
                    height: 18, fontSize: "0.65rem", textTransform: "capitalize",
                    bgcolor: alpha(c.status === "active" ? "#10b981" : "#94a3b8", 0.15),
                    color: c.status === "active" ? "#10b981" : "#94a3b8",
                    fontWeight: 600,
                  }}
                />
              </Stack>
            </Box>
            <Metric mini label="Leads" value={c.total_leads} />
            <Metric mini label="In progress" value={c.in_progress} />
            <Metric mini label="Connected" value={c.connected} color="#10b981" />
            <Metric mini label="Replied" value={c.replied} color="#f59e0b" />
            <Metric mini label="Conn. rate" value={`${c.connection_rate.toFixed(1)}%`} />
            <Metric mini label="Reply rate" value={`${c.reply_rate.toFixed(1)}%`} />
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

const Metric = ({ label, value, color, mini }) => {
  const theme = useTheme();
  return (
    <Box>
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block", fontSize: mini ? "0.7rem" : "0.75rem" }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 700, fontSize: mini ? "0.95rem" : "1rem", color: color || theme.palette.text.primary }}>
        {value}
      </Typography>
    </Box>
  );
};

const InboxQueueSection = ({ inbox, queue, leadQuality }) => {
  const theme = useTheme();
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} md={4}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, height: "100%" }}>
          <SectionTitle>Inbox</SectionTitle>
          <Stack spacing={1.25}>
            <RowStat icon={InboxIcon} label="Unread messages" value={inbox?.unread_messages || 0} color="#f59e0b" />
            <RowStat icon={MessageSquare} label="Active conversations" value={inbox?.active_conversations || 0} color="#0A66C2" />
            <RowStat icon={Clock} label="Snoozed" value={inbox?.snoozed_conversations || 0} color="#94a3b8" />
            <RowStat
              icon={Timer}
              label="Avg response time"
              value={inbox?.avg_response_minutes != null ? `${formatMinutes(inbox.avg_response_minutes)}` : "—"}
              color="#8b5cf6"
            />
          </Stack>
        </Paper>
      </Grid>
      <Grid item xs={12} md={4}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, height: "100%" }}>
          <SectionTitle>Queue & System Health</SectionTitle>
          <Stack spacing={1.25}>
            <RowStat icon={ListFilter} label="Pending next 24h" value={queue?.pending_next_24h || 0} color="#6366f1" />
            <RowStat icon={AlertTriangle} label="Overdue" value={queue?.overdue || 0} color={queue?.overdue ? "#f59e0b" : "#94a3b8"} />
            <RowStat icon={Activity} label="Stuck (locked > 1h)" value={queue?.stuck_locked || 0} color={queue?.stuck_locked ? "#ef4444" : "#94a3b8"} />
            <RowStat icon={XCircle} label="Failed today" value={queue?.failed_today || 0} color={queue?.failed_today ? "#ef4444" : "#94a3b8"} />
          </Stack>
        </Paper>
      </Grid>
      <Grid item xs={12} md={4}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, height: "100%" }}>
          <SectionTitle>Lead Quality</SectionTitle>
          <Stack spacing={1.25}>
            <RowStat icon={CheckCircle2} label="Validated" value={leadQuality?.validated || 0} color="#10b981" />
            <RowStat icon={Clock} label="Pending validation" value={leadQuality?.pending || 0} color="#f59e0b" />
            <RowStat icon={XCircle} label="Rejected" value={leadQuality?.rejected || 0} color="#ef4444" />
            <RowStat icon={Users} label="Validated, no campaign" value={leadQuality?.validated_not_in_campaign || 0} color="#6366f1" />
            <RowStat icon={AlertTriangle} label="Blacklisted" value={leadQuality?.blacklisted || 0} color="#94a3b8" />
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
};

const RowStat = ({ icon: Icon, label, value, color }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box sx={{
        width: 28, height: 28, borderRadius: "8px", display: "flex",
        alignItems: "center", justifyContent: "center",
        bgcolor: alpha(color || theme.palette.primary.main, 0.12),
      }}>
        <Icon size={14} color={color || theme.palette.primary.main} strokeWidth={1.75} />
      </Box>
      <Typography variant="body2" sx={{ flex: 1, color: theme.palette.text.secondary }}>{label}</Typography>
      <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>{value}</Typography>
    </Stack>
  );
};

const formatMinutes = (mins) => {
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = mins / 60;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
};

// ── Main ────────────────────────────────────────────────────────────────

const OutreachDashboard = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { dashboard, loading } = useSelector((state) => state.linkedinOutreach);

  useEffect(() => {
    dispatch(fetchLIDashboard());
  }, [dispatch]);

  if (loading && !dashboard) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={32} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  const d = dashboard || {};

  return (
    <Box>
      <OverviewSection d={d} />
      <EngagementSection d={d} />
      <ActionMixSection mix={d.action_mix} />

      {/* Funnel + Daily Activity — heights matched via height:100% on both */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <FunnelSection funnel={d.funnel} />
        </Grid>
        <Grid item xs={12} md={7}>
          <TimeseriesSection />
        </Grid>
      </Grid>

      {/* Trend line — full width */}
      <Box sx={{ mb: 3 }}>
        <RatesTrendSection />
      </Box>

      <AccountsHealthSection />
      <CampaignsSummarySection />

      <InboxQueueSection
        inbox={d.inbox}
        queue={d.queue_health}
        leadQuality={d.lead_quality}
      />
    </Box>
  );
};

export default OutreachDashboard;
